import { DataVariants, DisplayValue, IDataSetColumn } from '@adaptiva/core/common';
import { EBaseFilterCondition } from '@adaptiva/core/filters';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { EJavaClassNames } from '@adaptiva/data-service';
import { IMetadataObject, MetadataObject, MetadataSerialization } from '@adaptiva/metadata/common';
import { FilterComponent, FilterComponentMenuController, IFilterNode } from '@adaptiva/widgets/filter';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, forwardRef } from '@angular/core';
import { take, timer } from 'rxjs';
import { PrerequisiteTreeMenuController } from './menus';

@Component({
	standalone: false,
	selector: 'ada-prerequisite-tree',
	templateUrl: './prerequisite-tree.component.html',
	styleUrls: [
		'../../../../widgets/filter/src/filter.component.scss',
		'../../../../widgets/filter/src/base-filter.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		PrerequisiteTreeMenuController,
		WidgetService,
		{
			provide: FilterComponentMenuController,
			useExisting: forwardRef(() => PrerequisiteTreeMenuController),
		},
		{
			provide: BaseWidgetComponent,
			useExisting: forwardRef(() => PrerequisiteTreeComponent)
		},
	]
})
export class PrerequisiteTreeComponent extends FilterComponent implements AfterViewInit {

	@Input()
	public metadataObject: IMetadataObject;

	public readonly metadataClassName: EJavaClassNames = EJavaClassNames.MetadataObject;

	public override overlayFilterType: EBaseFilterCondition = EBaseFilterCondition.NUMBER_EQUALS;

	public readonly filterTypeOptions: DisplayValue<EBaseFilterCondition>[] = [
		new DisplayValue<EBaseFilterCondition>('Number Equals', EBaseFilterCondition.NUMBER_EQUALS),
		new DisplayValue<EBaseFilterCondition>('Number Any of These Values', EBaseFilterCondition.NUMBER_ANY),
	];

	// columns are hardcoded for this component
	public override get columns(): IDataSetColumn[] {
		return [{
			columnName: '',
			type: DataVariants.WHOLE_NUMBER,
			columnDisplay: 'Product ID'
		}];
	}

	public get isMulti(): boolean {
		return this.overlayFilterType === EBaseFilterCondition.NUMBER_ANY;
	}

	public override openOperatingConditionOverlay(_node?: IFilterNode): void {
		this.overlayNode = _node || undefined;
		this.overlayColumnName = this.columns[0].columnName;
		this.overlayValue = this.overlayEditing ? _node?.userInput.value : undefined;
		this.overlayFilterType = _node?.filterType ?? EBaseFilterCondition.NUMBER_EQUALS;

		this.operatingConditionOverlay?.open();
	}

	public validateProductSelection(_selections: MetadataObject[]): Error[] {
		const _errors: Error[] = [];
		let _object: MetadataObject;

		if (!_selections?.length)
			return [];

		for (const _selection of _selections) {
			_object = _selection;

			if (_object.objectId === this.metadataObject.objectId)
				_errors.push(new Error('Cannot add self as a prerequisite'));

			if (_object.javaClassName === EJavaClassNames.SoftwareProduct) {
				if (this.metadataObject.javaClassName === EJavaClassNames.SoftwareRelease) {
					if (_object.objectId === MetadataSerialization.getPropertyValue(this.metadataObject, 'Relationships', 'Product'))
						_errors.push(new Error('Cannot set parent object as prerequisite'));
				} else if (
					this.metadataObject.javaClassName === EJavaClassNames.SoftwareReleaseGroup ||
					this.metadataObject.javaClassName === EJavaClassNames.SoftwarePatch
				) {
					if (_object.objectId === MetadataSerialization.getPropertyValue(this.metadataObject, 'Relationships', 'Parent'))
						_errors.push(new Error('Cannot set parent object as prerequisite'));
				}
			}
		}

		return _errors;
	}

	public setOverlayFilterType(_type: EBaseFilterCondition): void {
		if (this.overlayFilterType === _type)
			return;

		if (
			this.overlayFilterType === EBaseFilterCondition.NUMBER_ANY &&
			Array.isArray(this.overlayValue) &&
			this.overlayValue.length > 1
		)
			this.overlayValue = this.overlayValue[0];

		this.overlayFilterType = _type;
		timer(0).pipe(take(1)).subscribe(this.detectChanges.bind(this));
	}

}
